import { UseFormGraphQlOptions } from '@graphcommerce/ecommerce-ui'
import {
  AddProductsToCartForm,
  AddProductsToCartMutation,
  AddProductsToCartMutationVariables,
  ProductShortDescription,
} from '@graphcommerce/magento-product'
import { ConfigurableProductPageGallery } from '@graphcommerce/magento-product-configurable'
import { Box, Typography } from '@mui/material'
import { DefaultPageQuery } from '../../../graphql/DefaultPage.gql'
import { Product } from '../../../lib/types'
import { CallADruggistQuery } from '../../Dialogs/MoreInformationDialog/CallADruggist.gql'
import { LayoutQuery } from '../../Layout/Layout.gql'
import { Usps } from '../../Usps/Usps'
import { UspsQueryFragment } from '../../Usps/UspsQueryFragment.gql'
import { GlobalProductCartAction } from '../GlobalProductCartAction'
import { MoreOfThisBrand } from '../MoreOfThisBrand'
import { ProductPageAccordion } from '../ProductPageAccordion/Accordion'
import { PromoSidebar } from '../PromoSidebar'
import { PromotionLabel } from '../PromotionLabel/PromotionLabel'
import { PromotionWrapper } from './PromotionWrapper'
import { Trans } from '@lingui/react'

type ProductPageProductInfoProps = {
  product: Product
  pages: NonNullable<DefaultPageQuery['pages']>
} & CallADruggistQuery &
  Pick<LayoutQuery, 'sidebarUsps'> &
  UseFormGraphQlOptions<AddProductsToCartMutation, AddProductsToCartMutationVariables> &
  UspsQueryFragment

export function ProductPageProductInfo(props: ProductPageProductInfoProps) {
  const { product, infoForm, callADruggist, sidebarUsps, pages, keurmerkUsps, ...formProps } = props
  const { label_display, label_qty, label_percent, label_price, price_range, promotion_type } =
    product

  return (
    <Box
      sx={{
        '@media (min-width: 1300px)': {
          display: 'grid',
          gridTemplateColumns: 'auto auto',
        },
      }}
    >
      <AddProductsToCartForm {...formProps}>
        <GlobalProductCartAction />
        <Box sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              zIndex: 2,
              '& .promotionLabel': { left: '25px', top: '25px', transform: 'scale(1.5)' },
            }}
          >
            <PromotionLabel
              label_display={label_display}
              label_qty={label_qty}
              label_percent={label_percent}
              label_price={label_price}
              promotion_type={promotion_type}
              price_range={price_range}
            />
          </Box>
          <ConfigurableProductPageGallery
            url_key={product.url_key}
            media_gallery={product.media_gallery}
            aspectRatio={[1, 1]}
            sx={(theme) => ({
              mb: 0,
              '& .MuiTypography-h3:first-of-type': {
                typography: theme.typography.h2,
                fontWeight: theme.typography.fontWeightLight,
                mb: theme.spacings.xs,
                [theme.breakpoints.down('md')]: {
                  fontSize: '20px',
                  lineHeight: 1.6,
                },
              },
              '& .SidebarGallery-root': {
                alignItems: 'start',
              },
              '& .SidebarGallery-root.zoomed': {
                position: 'absolute',
                inset: 0,
              },
              '& .SidebarGallery-sidebar': {
                pb: 0,
                px: theme.spacings.md,

                [theme.breakpoints.down('md')]: {
                  '& .price': {
                    fontSize: '35px',
                    paddingRight: `calc(${theme.spacings.lg} * 1.5)`,
                  },
                  '& .MuiIconButton-root': {
                    px: '12px',
                  },
                },
              },
              '& .Scroller-root picture img': {
                p: theme.spacings.xxs,
              },
              '& .SidebarGallery-scrollerContainer': {
                top: 0,
                '&:not(.zoomed)': {
                  width: 'auto',
                  [theme.breakpoints.up('md')]: {
                    height: '550px',
                  },
                  [theme.breakpoints.down('md')]: {
                    paddingTop: '38vh',
                  },
                },
                '&.zoomed': {
                  top: 50,
                  [theme.breakpoints.up('md')]: {
                    top: 0,
                  },
                },
                '& .SidebarGallery-bottomCenter': {
                  [theme.breakpoints.down('md')]: {
                    '&:not(.zoomed)': {
                      top: '98%',
                    },
                  },
                },
              },
              [theme.breakpoints.up('md')]: {
                '& .SidebarGallery-root:not(.zoomed)': {
                  gridTemplateColumns: '1fr 1fr',
                },
              },
            })}
          >
            <PromotionWrapper product={product} infoForm={infoForm} callADruggist={callADruggist} />
            <ProductShortDescription short_description={product?.short_description} />
            <Typography
              variant='body2'
              sx={(theme) => ({ my: theme.spacings.xs, fontWeight: 'bold' })}
            >
              <Trans id='Please note: not all products are available in our stores' />
            </Typography>
            <Box
              sx={(theme) => ({
                padding: theme.spacings.xxs,
                backgroundColor: theme.palette.background.default,
                borderRadius: '10px',
                '& a': {
                  color: 'inherit',
                  textDecoration: 'underline',
                },

                [theme.breakpoints.down('md')]: {
                  mb: theme.spacings.lg,
                },
              })}
            >
              <Usps usps={sidebarUsps} size='small' />
            </Box>
          </ConfigurableProductPageGallery>
          <MoreOfThisBrand product={product} />
          <ProductPageAccordion
            product={product}
            keurmerkUsps={keurmerkUsps}
            callADruggist={callADruggist}
          />
        </Box>
      </AddProductsToCartForm>

      <PromoSidebar pages={pages} product={product} />
    </Box>
  )
}
